import type { SmartChipGrid } from './api-types';
import type { GenePanelConfigurationId } from './gene-panels';

export type WaterOverlaySpec = {
  primary?: readonly string[];
  secondary?: readonly string[];
};

/**
 * H2O placeholder wells configuration for specific SmartChip configurations.
 *
 * Certain configurations require specific wells to be set to 'H2O' (water) as placeholders.
 * This decoration is applied in two places:
 * 1. UI display: src/client/views/lab-view/pages/smartchip-run-page.tsx (around line 846)
 * 2. MyDesign file generation: src/server/modules/lab/handlers/handle-get-smartchip-mydesign.ts (around line 36)
 *
 * IMPORTANT: If you modify this configuration, you must ensure both places continue to apply
 * the decoration correctly. The mydesign file must match what users see in the UI.
 */
export const WATER_PLACEHOLDER_WELLS: Partial<Record<GenePanelConfigurationId, WaterOverlaySpec>> = {
  '248:6': { primary: ['A4', 'B4', 'C4', 'D4'] },
  '296:5': { primary: ['O3', 'O4', 'P3', 'P4'] },
  '80:21': { primary: ['P4'] },
};

export function decorateGridWithWater(grid: SmartChipGrid, wells: readonly string[] | undefined): SmartChipGrid {
  if (!wells || wells.length === 0) {
    return grid;
  }
  const rowIndexByLabel = new Map(grid.rowLabels.map((label, index) => [label.trim().toUpperCase(), index]));
  const columnIndexByLabel = new Map(
    grid.columnLabels.map((label, index) => [String(label).trim().toUpperCase(), index]),
  );
  let mutated = false;
  const nextValues = grid.values.map(row => row.slice());
  for (const well of wells) {
    const trimmed = well.trim();
    const match = /^([A-Za-z]+)(\d+)$/.exec(trimmed);
    if (!match) {
      continue;
    }
    const rowIndex = rowIndexByLabel.get(match[1].toUpperCase());
    const columnIndex = columnIndexByLabel.get(match[2]);
    if (rowIndex == null || columnIndex == null) {
      continue;
    }
    const rowValues = nextValues[rowIndex];
    const currentValue = rowValues[columnIndex];
    if (currentValue === 'H2O') {
      continue;
    }
    rowValues[columnIndex] = 'H2O';
    mutated = true;
  }
  if (!mutated) {
    return grid;
  }
  return {
    ...grid,
    values: nextValues,
  };
}

export function decorateSampleLayoutWithWater<T extends { primary: SmartChipGrid; secondary?: SmartChipGrid | null }>(
  layout: T,
  spec: WaterOverlaySpec | undefined,
): T {
  if (!spec) {
    return layout;
  }
  let mutated = false;
  const primary = spec.primary?.length ? decorateGridWithWater(layout.primary, spec.primary) : layout.primary;
  if (primary !== layout.primary) {
    mutated = true;
  }
  let secondary = layout.secondary;
  if (layout.secondary && spec.secondary?.length) {
    const decoratedSecondary = decorateGridWithWater(layout.secondary, spec.secondary);
    if (decoratedSecondary !== layout.secondary) {
      secondary = decoratedSecondary;
      mutated = true;
    }
  }
  if (!mutated) {
    return layout;
  }
  return {
    ...layout,
    primary,
    secondary,
  };
}

export function decorateSampleLayoutsWithWater<T extends { primary: SmartChipGrid; secondary?: SmartChipGrid | null }>(
  layouts: readonly T[],
  configurationId: GenePanelConfigurationId | null,
): T[] {
  if (!configurationId) {
    return [...layouts];
  }
  const spec = WATER_PLACEHOLDER_WELLS[configurationId];
  if (!spec) {
    return [...layouts];
  }
  const decorated: T[] = layouts.map(layout => decorateSampleLayoutWithWater(layout, spec));
  const hasChanges = decorated.some((layout, index) => layout !== layouts[index]);
  return hasChanges ? decorated : [...layouts];
}
